"use client";

import { Facebook, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../AnimatedSection';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-brand-green text-white py-16 overflow-hidden" id="footer">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Address Section */}
          <AnimatedSection delay={0.2}>
            <h3 className="text-2xl font-light mb-6">{t("footer.address")}</h3>
            <p className="text-sm mb-2">{t("footer.visit_us")}</p>
            <div className="flex items-start space-x-2">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              >
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
              </motion.div>
              <p className="text-sm">
                IFSULDEMINAS - CAMPUS POÇOS DE CALDAS {t("footer.innovation_city")} <br />
                Av. Dirce Pereira Rosa, 300 <br />
                Poços de Caldas, MG - Brasil<br />
              </p>
            </div>
          </AnimatedSection>

          {/* Contact Section */}
          <AnimatedSection delay={0.4}>
            <h3 className="text-2xl font-light mb-6">{t("footer.contact")}</h3>
            <p className="text-sm mb-4">{t("footer.contact_description")}</p>
            <div className="space-y-3">
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Mail className="w-5 h-5" />
                <a href="mailto:contato@regiaovulcanica.org.br" className="text-sm hover:text-brand-orange transition-colors">
                  contato@regiaovulcanica.org.br
                </a>
              </motion.div>
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Phone className="w-5 h-5" />
                <a href="https://wa.me/55359981965190" target="_blank" className="text-sm hover:text-brand-orange transition-colors">
                  +55 (35) 99819 6519
                </a>
              </motion.div>
            </div>

            {/* Social Media */}
            <div className="flex space-x-4 mt-6">
              <motion.a
                target='_blank'
                href="https://www.facebook.com/regiaovulcanica"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </motion.a>
              <motion.a
                target='_blank'
                href="https://www.instagram.com/regiaovulcanica/"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </motion.a>
            </div>
          </AnimatedSection>

          {/* Support Section */}
          <AnimatedSection delay={0.6}>
            <h3 className="text-2xl font-light mb-6">{t("footer.support")}</h3>
            <div className="flex space-x-8">
              <motion.div
                className="p-4 rounded-lg"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Image
                  src="/images/footer/mda.png"
                  alt="MDA"
                  width={946}
                  height={186}
                  className="h-20 w-auto object-contain"
                />
              </motion.div>
              <motion.div
                className="p-4 rounded-lg"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Image
                  src="/images/footer/sebrae.png"
                  alt="SEBRAE"
                  width={523}
                  height={283}
                  className="h-20 w-auto object-contain"
                />
              </motion.div>
              <motion.div
                className="p-4 rounded-lg"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Image
                  src="/images/footer/instituto.png"
                  alt="IFSULDEMINAS"
                  width={1209}
                  height={1619}
                  className="h-20 w-auto object-contain"
                />
              </motion.div>
            </div>
          </AnimatedSection>
        </div>

        {/* Copyright */}
        <motion.div
          className="mt-16 pt-8 border-t border-white/10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
        >
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} Região Vulcânica - {t("footer.copyright")}
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
